import React, { useState, useEffect } from "react";
import Order from "../../../actions/order";
import Loading from "../../../components/customHelpers/Loading";
import {
  Box,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import Notify from "../../../components/customHelpers/Notify";
import Popup from "../../../components/customHelpers/Popup";
import CustomerOrderPage from "../../../components/forms/allOrdersForCustomer";
import { formatNumber } from "../../../utils/utils";
import { statusBadge } from "../../../components/statusBadge";

const CustomerOrder = ({ id }) => {
  const [orders, setOrders] = useState([]);
  const { NotifyMessage } = Notify();
  const { viewCustomerOrders } = Order();
  const [values, setValues] = useState({ loading: false, reload: false });
  const [totals, setTotals] = useState({ totalPaid: 0, totalUnpaid: 0 });
  const { loading } = values;
  const [openPopup, setOpenPopup] = useState(false);

  useEffect(() => {
    getAllOrders();
  }, [id]);

  const getAllOrders = () => {
    setValues({ ...values, loading: true });
    viewCustomerOrders(id).then((data) => {
      if (data.err) {
        NotifyMessage({ message: data.err, type: "error" });
      } else if (data.orders) {
        setValues({ ...values, loading: false });

        const filteredOrders = data.orders.filter(
          (order) => order.status !== "Pending"
        );
        setOrders(filteredOrders);

        let totalPaid = 0;
        let totalUnpaid = 0;

        filteredOrders.forEach((order) => {
          const orderPaid = parseFloat(order.paid) || 0;
          const totalOrderPrice =
            order.orderProducts?.reduce(
              (sum, product) => sum + (parseFloat(product.totalPrice) || 0),
              0
            ) || 0;

          totalPaid += orderPaid;
          totalUnpaid += totalOrderPrice - orderPaid;
        });

        setTotals({
          totalPaid: totalPaid.toFixed(2),
          totalUnpaid: totalUnpaid.toFixed(2),
        });
      }
    });
  };

  const calculateTotalPrice = (data) => {
    let totalPrice = 0;
    if (data?.length > 0) {
      data.forEach((order) => {
        totalPrice += parseFloat(order.totalPrice);
      });
      return totalPrice.toFixed(2);
    }
    return "0.00";
  };

  return (
    <>
      <Card
        sx={{
          backgroundColor: "background.paper",
          borderRadius: 1,
          boxShadow: 2,
          minWidth: "90%",
        }}
      >
        <CardContent>
          <Grid item xs={12}>
            {loading ? (
              <Loading />
            ) : (
              <Box
                sx={{
                  maxHeight: 200,
                  overflow: "auto",
                  "&::-webkit-scrollbar": { width: 1 },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "grey.400",
                    borderRadius: 1,
                  },
                  "&::-webkit-scrollbar-track": { background: "transparent" },
                }}
              >
                <Table
                  stickyHeader
                  size="small"
                  sx={{
                    "& .MuiTableCell-root": {
                      padding: "2px 6px",
                      fontSize: "0.85rem",
                      lineHeight: 1.4,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    },
                    "& .MuiTableRow-root": { height: 32 },
                    "& .MuiTableHead-root .MuiTableCell-root": {
                      fontWeight: "bold",
                      backgroundColor: "#f5f5f5",
                    },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: 80 }}>ID</TableCell>
                      <TableCell sx={{ width: 140 }}>Assigned To</TableCell>
                      <TableCell sx={{ width: 100 }}>Total Price</TableCell>
                      <TableCell sx={{ width: 80 }}>Paid</TableCell>
                      <TableCell sx={{ width: 100 }}>Unpaid</TableCell>
                      <TableCell sx={{ width: 100 }}>Status</TableCell>
                      <TableCell sx={{ width: 160 }}>Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {orders.length > 0 ? (
                      orders.slice(0, 5).map((row) => (
                        <TableRow key={row.id}>
                          <TableCell>
                            {row.orderId
                              ? row.orderId
                              : `${row.id.slice(0, 7)}--`}
                          </TableCell>
                          <TableCell>{row.staff?.fullName}</TableCell>
                          <TableCell>
                            {row?.orderProducts?.length > 0
                              ? formatNumber(
                                  calculateTotalPrice(row.orderProducts)
                                )
                              : "0.00"}
                          </TableCell>
                          <TableCell>{formatNumber(row.paid || 0)}</TableCell>
                          <TableCell>
                            {formatNumber(
                              (Number(calculateTotalPrice(row.orderProducts)) ||
                                0) - (Number(row.paid) || 0)
                            )}
                          </TableCell>
                          <TableCell>
                            {statusBadge(row.status || "Unknown")}
                          </TableCell>
                          <TableCell>
                            {new Date(row.createdAt).toLocaleString()}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          No Orders Available
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>
            )}
          </Grid>

          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "flex-start", sm: "center" },
              gap: 2,
              m: 1,
              bgcolor: "background.paper",
              borderRadius: 1,
              p: 1,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 1,
                width: { xs: "100%", sm: "auto" },
              }}
            >
              <Typography variant="body2" fontWeight="bold" whiteSpace="nowrap">
                Total Paid
              </Typography>
              <Typography variant="body2">
                {formatNumber(totals.totalPaid)}
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 1,
                width: { xs: "100%", sm: "auto" },
              }}
            >
              <Typography variant="body2" fontWeight="bold" whiteSpace="nowrap">
                Total Balance
              </Typography>
              <Typography variant="body2">
                {formatNumber(totals.totalUnpaid)}
              </Typography>
            </Box>

            <Box
              component="span"
              onClick={() => setOpenPopup(true)}
              sx={{
                ml: { xs: 0, sm: 3 },
                mt: { xs: 1, sm: 0 },
                color: "primary.main",
                fontWeight: "bold",
                cursor: "pointer",
                "&:hover": {
                  textDecoration: "underline",
                  color: "primary.dark",
                },
              }}
            >
              See more
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Popup
        title="Customer Orders"
        openPopup={openPopup}
        setOpenPopup={setOpenPopup}
      >
        <CustomerOrderPage orders={orders} />
      </Popup>
    </>
  );
};

export default CustomerOrder;
